import { useCallback } from 'react';

import { useBasketAddProduct, useGetBasket } from '@api/basket';
import { ProductDetail } from '@api/catalog';

import { useCartContext } from '@context/cart';

import { getItemForMetrics, metricsPushEvent } from '@scripts/helpers';
import { canAddToCart, isQtyValid, toOfferSignals } from '@scripts/offerAvailability';

import { useGetUserData } from './useGetUserData';

interface ProductWithSelection extends ProductDetail {
    is_selected?: boolean;
}

export const useChangeQty = (product?: ProductWithSelection) => {
    const { user } = useGetUserData();

    const { basketProducts, setBasketProducts } = useCartContext();
    const { mutateAsync: setProduct, isPending } = useBasketAddProduct();
    const { data: apiBasketData } = useGetBasket(Boolean(user));

    const changeQty = useCallback(
        async (value: number, id: number, setIsLoadingBasket?: (val: boolean) => void) => {
            const authBasketItem = user
                ? apiBasketData?.data?.find(item => (item.offer_id ?? item.id) === id)
                : undefined;
            const guestBasketItem = basketProducts.find(item => item.offer_id === id);
            const availabilitySource = product ?? authBasketItem ?? guestBasketItem;

            if (value > 0) {
                if (!availabilitySource) {
                    return false;
                }

                const signals = toOfferSignals(availabilitySource);

                if (
                    !canAddToCart({
                        availability_status: signals.availabilityStatus,
                        max_order_qty: signals.maxOrderQty,
                    }) ||
                    !isQtyValid(value, signals.multiple, signals.maxOrderQty)
                ) {
                    return false;
                }
            }

            if (user) {
                if (setIsLoadingBasket) setIsLoadingBasket(true);

                const is_selected =
                    product && 'is_selected' in product
                        ? product.is_selected !== false
                        : authBasketItem
                          ? authBasketItem.is_selected !== false
                          : true;

                try {
                    await setProduct([{ offer_id: id, qty: value, is_selected }]);
                } catch {
                    return false;
                }
            } else {
                if (value) {
                    setBasketProducts(prevBasketProducts => {
                        const updated = prevBasketProducts.map(item =>
                            item.offer_id === id ? { ...item, qty: value } : item
                        );

                        return updated;
                    });
                } else {
                    setBasketProducts(prevBasketProducts => prevBasketProducts.filter(item => item.offer_id !== id));
                }
            }

            metricsPushEvent({
                event: value ? 'add_to_cart' : 'remove_from_cart',
                ecommerce: {
                    items: [
                        {
                            ...(product ? getItemForMetrics(product) : { item_id: id }),
                            quantity: value,
                        },
                    ],
                },
            });

            return true;
        },
        [user, basketProducts, setBasketProducts, setProduct, apiBasketData, product]
    );

    return { changeQty, isPending };
};
